import { produce } from 'immer';
import {
  type Cart,
  type CartPairing,
  type CartProduct,
  type PairingBundle,
  type ProductBundle,
} from '@hungryroot/rest';

export function addOptimisticCartProduct(
  cart: Cart,
  product: CartProduct,
  options: { addVolume?: boolean }
): Cart {
  const { addVolume } = options;
  const { volume: productVolume, is_ambient } = product;
  const optimisticBundle = makeOptimisticSkuBundle(product);
  return produce(cart, (draft) => {
    draft.bundles.push(optimisticBundle);
    draft.products.push(product);
    if (addVolume !== false) {
      draft.volume += productVolume;

      if (is_ambient === false) {
        draft.cold_volume += productVolume;
      }
    }
  });
}

function addPairingBundleOptimisticCart(
  cart: Cart,
  bundle: PairingBundle,
  ingredients: CartProduct[],
  options: { addVolume?: boolean }
) {
  const { addVolume } = options;
  const cartProducts = cart.products.reduce(
    (seed, product) => {
      const { id: productId } = product;
      seed[productId] = true;
      return seed;
    },
    {} as Record<string, true>
  );
  return produce(cart, (draft) => {
    draft.bundles.push(bundle);
    ingredients.forEach((ingredient) => {
      const { id: ingredientId, volume, is_ambient } = ingredient;
      if (addVolume !== false) {
        draft.volume += volume;
        if (is_ambient === false) {
          draft.cold_volume += volume;
        }
      }
      if (cartProducts[ingredientId] !== undefined) {
        return;
      }
      draft.products.push(ingredient);
    });
  });
}

export function addPairingOptimisticCart(
  cart: Cart,
  pairing: CartPairing,
  ingredients: CartProduct[],
  options: { addVolume?: boolean }
): Cart {
  const virtualBundle = makeOptimisticPairingBundle(pairing, ingredients);
  return addPairingBundleOptimisticCart(
    cart,
    virtualBundle,
    ingredients,
    options
  );
}

export function bundleIsOptimistic(bundle: { id: number }): boolean {
  const { id } = bundle;
  return id < 0;
}

let optimisticBundleId = -1;

function getOptimisticBundleId() {
  return (optimisticBundleId -= 1);
}

function makeOptimisticPairingBundle(
  pairing: CartPairing,
  ingredients: CartProduct[]
): PairingBundle {
  return {
    id: getOptimisticBundleId(),
    is_from_bfl: false,
    is_featured: pairing.is_featured,
    is_promo: false,
    is_intact: true,
    order_details: ingredients.map(({ id: ingredientId }) => {
      return {
        quantity: 1,
        is_promo: false,
        issue: 0,
        occasion: 0,
        product: ingredientId,
      };
    }),
    pairing,
    type: {
      id: -100,
      create_date: '',
      modify_date: '',
      name: '',
    },
    points: pairing.points,
  };
}

function makeOptimisticSkuBundle(product: CartProduct): ProductBundle {
  return {
    id: getOptimisticBundleId(),
    is_intact: true,
    is_from_bfl: false,
    is_featured: false,
    is_promo: false,
    order_details: [
      {
        quantity: 1,
        is_promo: false,
        issue: 0,
        occasion: 0,
        product: product.id,
      },
    ],
    pairing: null,
    type: {
      id: -100,
      create_date: '',
      modify_date: '',
      name: '',
    },
    points: product.points,
  };
}
